'use client';

import Image from 'next/image';
import ContactForm from '@/components/contact/contact-form';
import ContactInfo from '@/components/contact/contact-info';
import LocationMap from '@/components/contact/location-map';
import SectionTitle from '@/components/home/section-title';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useTranslation } from '@/hooks/useTranslation';

export default function ContactPage() {
  const { t } = useTranslation('contact');

  return (
    <div>
      <div className="relative h-[50vh] bg-gradient-to-r from-blue-900 to-blue-700 flex items-center justify-center">
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src="https://images.pexels.com/photos/3845757/pexels-photo-3845757.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            alt="DentaWista Contact"
            fill
            className="object-cover opacity-20"
            priority
            sizes="100vw"
          />
        </div>
        <div className="container relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-display">
            {t('title')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            {t('subtitle')}
          </p>
        </div>
      </div>

      <div className="container py-12">
        <Breadcrumbs />
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 font-display">{t('description.line1')}</h2>
          <p className="text-lg text-gray-600">{t('description.line2')}</p>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-16">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xs text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-1 dark:text-white">{t('info.visitUs')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('address.line1')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('address.line2')}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xs text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-1 dark:text-white">{t('info.callUs')}</h3>
            <a href="tel:+905070808888" className="text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors block">+90 507 080 8888</a>
            <a href="tel:+905070808888" className="text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors block">{t('phone.emergency')}: +90 507 080 8888</a>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xs text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-1 dark:text-white">{t('info.emailUs')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">info@dentawista.com</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">support@dentawista.com</p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-xs text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-1 dark:text-white">{t('info.workingHours')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('hours.weekdays')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('hours.saturday')}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="text-center md:text-left">
            <SectionTitle>{t('form.title')}</SectionTitle>
            <ContactForm />
          </div>

          <div>
            <div className="text-center md:text-left ">
              <SectionTitle>{t('form.location')}</SectionTitle>
            </div>
            <LocationMap />
          </div>
        </div>
      </div>
    </div>
  );
}

